import { ColaboratorResponse, ColaboratorsRequest } from '@/app/models/avaliablePercent';
import { BranchResponse } from '@/app/models/branch';
import { Collaborator } from '@/app/models/collaborator';
import { useAddCollaboratorMutation, useFindBybranchAvaliablePercentQuery } from '@/app/services/avaliablePercetApi';
import { useBranchsAllQuery } from '@/app/services/branchApi';
import { useSearchPersonQuery } from '@/app/services/personApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import ModalLabel from '@/components/common/ModalLabel';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import PercentInput from '@/components/common/PercentInput';
import TextButton from '@/components/common/TextButton';
import { Box, MenuItem, Select, SelectChangeEvent, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ProjectTable from './ProjectTable';

interface NewEditTeacherPaymentProps {
  setDrawerOpen: (open: boolean) => void;
  editBranchCode?: string;
}

export default function NewEditValuePercent(props: NewEditTeacherPaymentProps) {
  const { t } = useTranslation(['avaliablePercentage', 'common']);

  const { setDrawerOpen, editBranchCode } = props;
  const theme = useTheme();

  const [assignedCollaborators, setAssignedCollaborators] = useState<ColaboratorResponse[]>([]);
  const [branchCode, setBranchCode] = useState(editBranchCode || '');

  const [newCollaboratorPercent, setNewCollaboratorPercent] = useState('');
  const [remainingPercent, setRemainingPercent] = useState('');

  const [collaborator, setCollaborator] = useState<Collaborator | null>(null);
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const { data: collaboratorsAutoComplete } = useSearchPersonQuery(
    {
      searchTerm: collaboratorSearch,
      available: true,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: collaboratorSearch.length < 1 },
  );

  const { data: buildings } = useFindBybranchAvaliablePercentQuery(editBranchCode as string, {
    skip: !editBranchCode,
  });

  const { data: branchs } = useBranchsAllQuery();

  useEffect(() => {
    if (buildings && buildings.length > 0 && branchCode) {
      const nullPerson = buildings.find((value) => value.person === null);
      if (nullPerson) {
        setRemainingPercent(nullPerson.percent.toFixed(2).replace('.', ','));
      }
      const newCollaborators = buildings
        .filter((value) => value.person !== null)
        .map((item) => {
          const newCollaborator: ColaboratorResponse = {
            id: item.person.id,
            name: item.person.name,
            email: item.person.email,
            badge: item.person.badge,
            percent: item.percent,
          };
          return newCollaborator;
        });
      setAssignedCollaborators(newCollaborators);
    }
  }, [branchCode, buildings]);

  const handleAddCollaborator = () => {
    if (!collaborator) return;

    const newCollaborator: ColaboratorResponse = {
      id: collaborator.id,
      name: collaborator.name,
      email: collaborator.email,
      badge: collaborator.badge,
      percent: parseFloat(newCollaboratorPercent.replace(',', '.')),
    };

    setAssignedCollaborators((prevValue) => [...prevValue, newCollaborator]);
    setNewCollaboratorPercent('0');
    setCollaborator('' as unknown as Collaborator);
    setCollaboratorSearch('');
  };

  const handleRemove = (removedCollaborator: number) => {
    setAssignedCollaborators((prevValue) => prevValue.filter((row) => row.id !== removedCollaborator));
  };

  const [authorizeMonthlyExpense] = useAddCollaboratorMutation();

  const handleSubmit = async () => {
    if (!branchCode) return;

    const requestBody: ColaboratorsRequest = {
      branchCode,
      percent: parseFloat(remainingPercent.replace(',', '.')),
      persons: assignedCollaborators.map((colab) => ({
        personId: colab.id,
        percent: colab.percent,
      })),
    };
    await authorizeMonthlyExpense(requestBody);

    toast.success(t('new.value.added'));
    setDrawerOpen(false);
  };

  const alreadyIncluded = (value: Collaborator) => {
    const filteredProjects = assignedCollaborators.filter((item) => item.id === value.id);
    return filteredProjects.length >= 1;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 1,
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('percentage.available')} subtitle={t('description.info') as string} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            mt: 2,
            width: '100%',
          }}
        >
          <Box>
            <Typography sx={{ display: 'flex' }} variant="fieldHeader">
              {t('region')}
            </Typography>
            <Select
              displayEmpty
              labelId="building-select-label"
              id="building-select"
              sx={{
                height: theme.spacing(4.75),
                width: theme.spacing(30),
                fontSize: theme.spacing(1.65),
                p: 0,
                m: 0,
              }}
              value={branchCode}
              onChange={(event: SelectChangeEvent<string>) => {
                setBranchCode(event.target.value);
              }}
            >
              <MenuItem disabled value="">
                <em>{t('select.branch')}</em>
              </MenuItem>
              {branchs?.content.map((item: BranchResponse) => (
                <MenuItem key={item.code} value={item.code} sx={{ fontSize: 13 }}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <Typography sx={{ display: 'flex' }} variant="fieldHeader">
              {t('allocation.percent')}
            </Typography>
            <PercentInput value={remainingPercent} onChange={setRemainingPercent} />
          </Box>
        </Box>
        <ModalLabel title={`${t('common:collaborator')}`} subtitle={t('instruction.collaborator') as string} />

        <GenericAutoComplete
          value={collaborator}
          onChange={(value) => {
            setCollaborator(value);
          }}
          equalityTest={(option, value) => option?.code === value?.code}
          getOptionLabel={(option) => `${option?.badge} - ${option?.name}` || ''}
          onSearch={(value) => {
            setCollaboratorSearch(value);
          }}
          options={
            collaboratorSearch !== '' && collaboratorsAutoComplete?.content ? collaboratorsAutoComplete?.content : []
          }
          placeholder={t('common:search.collaborator') as string}
          sx={{ width: theme.spacing(50) }}
        />

        <Box sx={{ mt: 4 }}>
          <Typography sx={{ display: 'flex' }} variant="fieldHeader">
            {t('allocation.percent')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PercentInput value={newCollaboratorPercent} onChange={setNewCollaboratorPercent} />
            <TextButton
              sx={{ ml: 'auto' }}
              text={`+ ${t('add')}`}
              disabled={!collaborator || !newCollaboratorPercent || alreadyIncluded(collaborator)}
              onClick={handleAddCollaborator}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <ProjectTable projects={assignedCollaborators as ColaboratorResponse[]} onRemove={handleRemove} />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          mb: 1,
          mt: 'auto',
        }}
      >
        <DrawerButtons
          onSave={handleSubmit}
          onCancel={() => setDrawerOpen(false)}
          disabled={
            !assignedCollaborators ||
            !branchCode ||
            !remainingPercent ||
            Number.isNaN(Number.parseFloat(remainingPercent.replace(',', '.'))) ||
            Number.parseFloat(remainingPercent.replace(',', '.')) === 0
          }
        />
      </Box>
    </Box>
  );
}
